import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  LayoutDashboard, Users, AlertTriangle, MessageCircle, History, 
  Shield, Ban, Clock, Eye, EyeOff, RefreshCw, Search, ChevronLeft,
  AlertCircle, FileWarning, Check, X, MoreVertical
} from 'lucide-react';

const AdminPanel = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dashboard state
  const [dashboardStats, setDashboardStats] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);

  // Reports state
  const [reports, setReports] = useState([]);
  const [reportTypeFilter, setReportTypeFilter] = useState('');

  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertTypeFilter, setAlertTypeFilter] = useState('');

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);

  // Action dialogs
  const [suspendDialog, setSuspendDialog] = useState({ open: false, userId: null });
  const [banDialog, setBanDialog] = useState({ open: false, userId: null });
  const [noteDialog, setNoteDialog] = useState({ open: false, userId: null });
  const [moderateDialog, setModerateDialog] = useState({ open: false, reportId: null });

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboard();
      setDashboardStats(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have admin access');
      } else {
        setError('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load users
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (userSearch) params.search = userSearch;
      if (userStatusFilter && userStatusFilter !== 'all') params.status = userStatusFilter;
      const response = await adminAPI.getUsers(params);
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [userSearch, userStatusFilter]);

  // Load user detail
  const loadUserDetail = useCallback(async (userId) => {
    try {
      setLoading(true);
      const response = await adminAPI.getUserDetail(userId);
      setUserDetail(response.data);
    } catch (err) {
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reports
  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (reportTypeFilter && reportTypeFilter !== 'all') params.type_filter = reportTypeFilter;
      const response = await adminAPI.getReports(params);
      setReports(response.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [reportTypeFilter]);

  // Load alerts
  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (alertTypeFilter && alertTypeFilter !== 'all') params.type_filter = alertTypeFilter;
      const response = await adminAPI.getAlerts(params);
      setAlerts(response.data);
    } catch (err) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [alertTypeFilter]);

  // Load audit log
  const loadAuditLog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAuditLog({});
      setAuditLog(response.data);
    } catch (err) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Tab change handlers
  useEffect(() => {
    switch (activeTab) {
      case 'dashboard':
        loadDashboard();
        break;
      case 'users':
        loadUsers();
        break;
      case 'reports':
        loadReports();
        break;
      case 'alerts':
        loadAlerts();
        break;
      case 'audit':
        loadAuditLog();
        break;
    }
  }, [activeTab, loadDashboard, loadUsers, loadReports, loadAlerts, loadAuditLog]);

  // User actions
  const handleSuspendUser = async (userId, duration, reason) => {
    try {
      await adminAPI.suspendUser({ user_id: userId, duration, reason });
      toast.success('User suspended');
      setSuspendDialog({ open: false, userId: null });
      loadUsers();
      if (selectedUser === userId) loadUserDetail(userId);
    } catch (err) {
      toast.error('Failed to suspend user');
    }
  };

  const handleBanUser = async (userId, reason) => {
    try {
      await adminAPI.banUser({ user_id: userId, reason });
      toast.success('User banned');
      setBanDialog({ open: false, userId: null });
      loadUsers();
      if (selectedUser === userId) loadUserDetail(userId);
    } catch (err) {
      toast.error('Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await adminAPI.unbanUser(userId);
      toast.success('User unbanned');
      loadUsers();
      if (selectedUser === userId) loadUserDetail(userId);
    } catch (err) {
      toast.error('Failed to unban user');
    }
  };

  const handleForceInvisible = async (userId) => {
    try {
      await adminAPI.forceInvisible(userId);
      toast.success('User forced invisible');
      loadUsers();
      if (selectedUser === userId) loadUserDetail(userId);
    } catch (err) {
      toast.error('Failed to force invisible');
    }
  };

  const handleResetAbuseScore = async (userId) => {
    try {
      await adminAPI.resetAbuseScore(userId);
      toast.success('Abuse score reset');
      loadUsers();
      if (selectedUser === userId) loadUserDetail(userId);
    } catch (err) {
      toast.error('Failed to reset abuse score');
    }
  };

  const handleAddNote = async (userId, note) => {
    try {
      await adminAPI.addNote({ user_id: userId, note });
      toast.success('Note added');
      setNoteDialog({ open: false, userId: null });
      if (selectedUser === userId) loadUserDetail(userId);
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  // Report actions
  const handleModerateReport = async (reportId, action, reason, newExpiry) => {
    try {
      await adminAPI.moderateReport({ report_id: reportId, action, reason, new_expiry: newExpiry });
      toast.success('Report moderated');
      setModerateDialog({ open: false, reportId: null });
      loadReports();
    } catch (err) {
      toast.error('Failed to moderate report');
    }
  };

  // Alert actions
  const handleMarkAlertReviewed = async (alertId) => {
    try {
      await adminAPI.markAlertReviewed({ alert_id: alertId });
      toast.success('Alert marked as reviewed');
      loadAlerts();
    } catch (err) {
      toast.error('Failed to mark alert reviewed');
    }
  };

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-red-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300 mb-4">{error}</p>
            <Button onClick={onClose} variant="outline">Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-white">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Exit Admin
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-cyan-400" />
            <h1 className="text-xl font-bold">Boat 360 Admin</h1>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => {
          if (activeTab === 'dashboard') loadDashboard();
          else if (activeTab === 'users') loadUsers();
          else if (activeTab === 'reports') loadReports();
          else if (activeTab === 'alerts') loadAlerts();
          else loadAuditLog();
        }} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      {/* Main Content */}
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-gray-800 min-h-[calc(100vh-73px)] border-r border-gray-700 p-4">
          <nav className="space-y-2">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'reports', label: 'Reports', icon: FileWarning },
              { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
              { id: 'audit', label: 'Audit Log', icon: History },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-cyan-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                data-testid={`admin-tab-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <main className="flex-1 p-6 overflow-auto max-h-[calc(100vh-73px)]">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && dashboardStats && (
            <div className="space-y-6" data-testid="admin-dashboard">
              <h2 className="text-2xl font-bold">Dashboard</h2>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-2">
                    <CardDescription className="text-gray-400">Active Users (15m)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-cyan-400">{dashboardStats.active_users_15m}</p>
                    <p className="text-sm text-gray-500">{dashboardStats.active_users_60m} in last hour</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-2">
                    <CardDescription className="text-gray-400">Active Trips</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-green-400">{dashboardStats.active_trips}</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-2">
                    <CardDescription className="text-gray-400">Reports (24h)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-yellow-400">
                      {dashboardStats.reports_24h_hazard + dashboardStats.reports_24h_activity}
                    </p>
                    <p className="text-sm text-gray-500">
                      {dashboardStats.reports_24h_hazard} hazards, {dashboardStats.reports_24h_activity} activities
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-2">
                    <CardDescription className="text-gray-400">Alerts (24h)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-red-400">{dashboardStats.alerts_24h_sos}</p>
                    <p className="text-sm text-gray-500">SOS alerts ({dashboardStats.alerts_24h_ok} I&apos;m OK)</p>
                  </CardContent>
                </Card>
              </div>

              {/* Flagged Users */}
              {dashboardStats.top_flagged_users?.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-400" />
                      Top Flagged Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dashboardStats.top_flagged_users.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                          <div>
                            <p className="font-medium">{user.display_name}</p>
                            <p className="text-sm text-gray-400">@{user.username}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant={user.abuse_score >= 60 ? 'destructive' : 'warning'} className="px-3">
                              Score: {user.abuse_score}
                            </Badge>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedUser(user.id);
                                loadUserDetail(user.id);
                                setActiveTab('users');
                              }}
                            >
                              View
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6" data-testid="admin-users">
              {selectedUser && userDetail ? (
                // User Detail View
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
                      <ChevronLeft className="w-5 h-5 mr-1" />
                      Back to Users
                    </Button>
                    <h2 className="text-2xl font-bold">User Details</h2>
                  </div>

                  {/* User Info Card */}
                  <Card className="bg-gray-800 border-gray-700">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>{userDetail.display_name}</CardTitle>
                          <CardDescription className="text-gray-400">@{userDetail.username} • {userDetail.email}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            userDetail.status === 'ACTIVE' ? 'success' :
                            userDetail.status === 'SUSPENDED' ? 'warning' : 'destructive'
                          }>
                            {userDetail.status}
                          </Badge>
                          {userDetail.abuse_score > 0 && (
                            <Badge variant={userDetail.abuse_score >= 60 ? 'destructive' : 'warning'}>
                              Abuse: {userDetail.abuse_score}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gray-700 p-3 rounded-lg">
                          <p className="text-sm text-gray-400">Reports (24h/7d)</p>
                          <p className="text-lg font-bold">{userDetail.reports_24h} / {userDetail.reports_7d}</p>
                        </div>
                        <div className="bg-gray-700 p-3 rounded-lg">
                          <p className="text-sm text-gray-400">SOS (24h/7d)</p>
                          <p className="text-lg font-bold">{userDetail.sos_24h} / {userDetail.sos_7d}</p>
                        </div>
                        <div className="bg-gray-700 p-3 rounded-lg">
                          <p className="text-sm text-gray-400">Dismiss Ratio</p>
                          <p className="text-lg font-bold">{(userDetail.dismiss_ratio * 100).toFixed(0)}%</p>
                        </div>
                        <div className="bg-gray-700 p-3 rounded-lg">
                          <p className="text-sm text-gray-400">Anomalies</p>
                          <p className="text-lg font-bold">{userDetail.teleport_count + userDetail.speed_anomaly_count}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        {userDetail.status !== 'BANNED' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => setSuspendDialog({ open: true, userId: selectedUser })}>
                              <Clock className="w-4 h-4 mr-1" />
                              Suspend
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => setBanDialog({ open: true, userId: selectedUser })}>
                              <Ban className="w-4 h-4 mr-1" />
                              Ban
                            </Button>
                          </>
                        )}
                        {userDetail.status === 'BANNED' && (
                          <Button variant="outline" size="sm" onClick={() => handleUnbanUser(selectedUser)}>
                            Unban
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => handleForceInvisible(selectedUser)}>
                          <EyeOff className="w-4 h-4 mr-1" />
                          Force Invisible
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleResetAbuseScore(selectedUser)}>
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Reset Score
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setNoteDialog({ open: true, userId: selectedUser })}>
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Add Note
                        </Button>
                      </div>

                      {/* Abuse Events */}
                      {userDetail.recent_abuse_events?.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium mb-2 text-gray-400">Recent Abuse Events</h4>
                          <ScrollArea className="h-48">
                            <div className="space-y-2">
                              {userDetail.recent_abuse_events.map((event, i) => (
                                <div key={i} className="bg-gray-700 p-2 rounded text-sm flex justify-between">
                                  <span>{event.type}</span>
                                  <span className="text-gray-400">Weight: {event.weight}</span>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      )}

                      {/* Moderation History */}
                      {userDetail.moderation_history?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium mb-2 text-gray-400">Moderation History</h4>
                          <ScrollArea className="h-48">
                            <div className="space-y-2">
                              {userDetail.moderation_history.map((action, i) => (
                                <div key={i} className="bg-gray-700 p-2 rounded text-sm">
                                  <div className="flex justify-between">
                                    <span className="font-medium">{action.action}</span>
                                    <span className="text-gray-400">{new Date(action.created_at).toLocaleDateString()}</span>
                                  </div>
                                  {action.reason && <p className="text-gray-400 mt-1">{action.reason}</p>}
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                // Users List View
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Users</h2>
                  
                  {/* Filters */}
                  <div className="flex gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input 
                        placeholder="Search users..." 
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && loadUsers()}
                        className="pl-10 bg-gray-800 border-gray-700"
                      />
                    </div>
                    <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                      <SelectTrigger className="w-40 bg-gray-800 border-gray-700">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="SUSPENDED">Suspended</SelectItem>
                        <SelectItem value="BANNED">Banned</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={loadUsers}>Search</Button>
                  </div>

                  {/* Users Table */}
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-700">
                            <tr>
                              <th className="text-left p-4 font-medium">User</th>
                              <th className="text-left p-4 font-medium">Status</th>
                              <th className="text-left p-4 font-medium">Abuse Score</th>
                              <th className="text-left p-4 font-medium">Last Seen</th>
                              <th className="text-left p-4 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user) => (
                              <tr key={user.id} className="border-t border-gray-700 hover:bg-gray-700/50">
                                <td className="p-4">
                                  <div>
                                    <p className="font-medium">{user.display_name}</p>
                                    <p className="text-sm text-gray-400">@{user.username}</p>
                                  </div>
                                </td>
                                <td className="p-4">
                                  <Badge variant={
                                    user.status === 'ACTIVE' ? 'success' :
                                    user.status === 'SUSPENDED' ? 'warning' : 'destructive'
                                  }>
                                    {user.status}
                                  </Badge>
                                </td>
                                <td className="p-4">
                                  <span className={user.abuse_score >= 30 ? 'text-orange-400 font-medium' : ''}>
                                    {user.abuse_score}
                                  </span>
                                </td>
                                <td className="p-4 text-gray-400">
                                  {user.last_seen ? new Date(user.last_seen).toLocaleString() : 'Never'}
                                </td>
                                <td className="p-4">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedUser(user.id);
                                      loadUserDetail(user.id);
                                    }}
                                  >
                                    View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="space-y-6" data-testid="admin-reports">
              <h2 className="text-2xl font-bold">Reports</h2>
              
              {/* Filters */}
              <div className="flex gap-4">
                <Select value={reportTypeFilter} onValueChange={setReportTypeFilter}>
                  <SelectTrigger className="w-40 bg-gray-800 border-gray-700">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="HAZARD">Hazards</SelectItem>
                    <SelectItem value="MARINE_ACTIVITY">Activities</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={loadReports}>Filter</Button>
              </div>

              {/* Reports List */}
              <div className="space-y-4">
                {reports.map((report) => (
                  <Card key={report.id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={report.type === 'HAZARD' ? 'destructive' : 'secondary'}>
                              {report.type}
                            </Badge>
                            <span className="font-medium">{report.subtype}</span>
                          </div>
                          <p className="text-sm text-gray-400">
                            By {report.creator_name || 'Unknown'} (@{report.creator_username || '?'})
                          </p>
                          <p className="text-sm text-gray-500">
                            Confidence: {report.confidence} | 
                            Votes: {report.vote_confirms} confirm, {report.vote_dismisses} dismiss
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setModerateDialog({ open: true, reportId: report.id })}
                          >
                            Moderate
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <div className="space-y-6" data-testid="admin-alerts">
              <h2 className="text-2xl font-bold">Alerts</h2>
              
              {/* Filters */}
              <div className="flex gap-4">
                <Select value={alertTypeFilter} onValueChange={setAlertTypeFilter}>
                  <SelectTrigger className="w-40 bg-gray-800 border-gray-700">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="SOS">SOS</SelectItem>
                    <SelectItem value="IM_OK">I&apos;m OK</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={loadAlerts}>Filter</Button>
              </div>

              {/* Alerts List */}
              <div className="space-y-4">
                {alerts.map((alert) => (
                  <Card key={alert.id} className={`bg-gray-800 border-gray-700 ${
                    alert.alert_type === 'SOS' && !alert.admin_reviewed ? 'border-red-500' : ''
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={alert.alert_type === 'SOS' ? 'destructive' : 'success'}>
                              {alert.alert_type}
                            </Badge>
                            {alert.admin_reviewed && (
                              <Badge variant="outline" className="text-green-400">Reviewed</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">
                            From {alert.sender_name || 'Unknown'} (@{alert.sender_username || '?'})
                          </p>
                          <p className="text-sm text-gray-500">
                            {alert.recipient_count} recipients • {new Date(alert.created_at).toLocaleString()}
                          </p>
                          {alert.message && <p className="mt-2 text-sm">{alert.message}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          {!alert.admin_reviewed && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleMarkAlertReviewed(alert.id)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Mark Reviewed
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-6" data-testid="admin-audit">
              <h2 className="text-2xl font-bold">Audit Log</h2>
              
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4 space-y-2">
                      {auditLog.map((action, i) => (
                        <div key={i} className="bg-gray-700 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant="outline">{action.action_type}</Badge>
                            <span className="text-sm text-gray-400">
                              {new Date(action.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm">
                            Admin: {action.admin_id?.slice(0, 8)}...
                            {action.target_user_id && ` → User: ${action.target_user_id.slice(0, 8)}...`}
                            {action.target_report_id && ` → Report: ${action.target_report_id.slice(0, 8)}...`}
                          </p>
                          {action.reason && (
                            <p className="text-sm text-gray-400 mt-1">Reason: {action.reason}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      {/* Suspend Dialog */}
      <SuspendDialog 
        open={suspendDialog.open}
        onClose={() => setSuspendDialog({ open: false, userId: null })}
        onConfirm={(duration, reason) => handleSuspendUser(suspendDialog.userId, duration, reason)}
      />

      {/* Ban Dialog */}
      <BanDialog 
        open={banDialog.open}
        onClose={() => setBanDialog({ open: false, userId: null })}
        onConfirm={(reason) => handleBanUser(banDialog.userId, reason)}
      />

      {/* Note Dialog */}
      <NoteDialog 
        open={noteDialog.open}
        onClose={() => setNoteDialog({ open: false, userId: null })}
        onConfirm={(note) => handleAddNote(noteDialog.userId, note)}
      />

      {/* Moderate Report Dialog */}
      <ModerateReportDialog 
        open={moderateDialog.open}
        onClose={() => setModerateDialog({ open: false, reportId: null })}
        onConfirm={(action, reason, newExpiry) => handleModerateReport(moderateDialog.reportId, action, reason, newExpiry)}
      />
    </div>
  );
};

// Suspend Dialog Component
const SuspendDialog = ({ open, onClose, onConfirm }) => {
  const [duration, setDuration] = useState('24h');
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700">
        <DialogHeader>
          <DialogTitle>Suspend User</DialogTitle>
          <DialogDescription className="text-gray-400">
            Temporarily restrict user access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Duration</label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="bg-gray-700 border-gray-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">24 Hours</SelectItem>
                <SelectItem value="7d">7 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Textarea 
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for suspension..."
              className="bg-gray-700 border-gray-600"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onConfirm(duration, reason); setReason(''); }}>
            Suspend
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Ban Dialog Component
const BanDialog = ({ open, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-red-400">Ban User</DialogTitle>
          <DialogDescription className="text-gray-400">
            Permanently ban this user from the platform.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Textarea 
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for ban..."
              className="bg-gray-700 border-gray-600"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => { onConfirm(reason); setReason(''); }}>
            Ban User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Note Dialog Component
const NoteDialog = ({ open, onClose, onConfirm }) => {
  const [note, setNote] = useState('');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700">
        <DialogHeader>
          <DialogTitle>Add Admin Note</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Textarea 
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter note..."
            className="bg-gray-700 border-gray-600"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onConfirm(note); setNote(''); }}>
            Add Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Moderate Report Dialog Component
const ModerateReportDialog = ({ open, onClose, onConfirm }) => {
  const [action, setAction] = useState('shadow_hide');
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700">
        <DialogHeader>
          <DialogTitle>Moderate Report</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Action</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="bg-gray-700 border-gray-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shadow_hide">Shadow Hide</SelectItem>
                <SelectItem value="remove">Remove (adds abuse event)</SelectItem>
                <SelectItem value="freeze_voting">Freeze Voting</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Textarea 
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason..."
              className="bg-gray-700 border-gray-600"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onConfirm(action, reason, null); setReason(''); }}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPanel;
